// =============================================================================
// TEAM SECTION COMPONENT
// =============================================================================

import React from 'react';
import { Linkedin, Twitter, Github, Mail } from 'lucide-react';
import type { ContentComponent } from '@/types';

const socialIconMap = {
  linkedin: Linkedin,
  twitter: Twitter,
  github: Github,
  email: Mail,
};

interface TeamSectionProps {
  component: ContentComponent;
}

/**
 * Team Section Component
 */
export const TeamSection: React.FC<TeamSectionProps> = ({ component }) => {
  return <TeamSectionImpl component={component} />;
};

/**
 * Render social links
 */
const renderSocialLinks = (social?: Record<string, string>) => {
  if (!social) return null;
  
  return Object.entries(social).map(([platform, url]) => {
    if (!url) return null;
    
    const IconComponent = socialIconMap[platform as keyof typeof socialIconMap];
    if (!IconComponent) return null;
    
    return (
      <a
        key={platform}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`social-link ${platform}`}
        aria-label={`${platform} profile`}
      >
        <IconComponent size={20} />
      </a>
    );
  });
};

/**
 * Team Section Implementation
 */
const TeamSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const team = metadata?.team || [];
  const columns = metadata?.columns || 3;
  
  return (
    <section id={id} className="content-section team-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`team-grid grid-cols-${columns}`}>
          {team.map((member: any, index: number) => (
            <div key={index} className="team-member">
              <div className="member-card">
                <div className="member-avatar">
                  {member.avatar ? (
                    <img src={member.avatar} alt={member.name} loading="lazy" />
                  ) : (
                    <div className="avatar-placeholder">
                      <span>{member.name.split(' ').map((n: string) => n[0]).join('')}</span>
                    </div>
                  )}
                </div>
                
                <div className="member-info">
                  <h3 className="member-name">{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  
                  {member.bio && (
                    <p className="member-bio">{member.bio}</p>
                  )}
                  
                  {member.social && Object.keys(member.social).length > 0 && (
                    <div className="member-social">
                      {renderSocialLinks(member.social)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { TeamSectionImpl };

